/**
 * A row of toggle chips over a small, closed option set.
 *
 * Extracted so the region and procurement-kind axes in the saved-search edit
 * sheet share one implementation instead of two near-identical JSX blocks —
 * see the Task 5 amendment. Both are "pick any of these few things", never a
 * dropdown: a dropdown hides how many are on, and the set is small enough that
 * every option can just be visible.
 */
export function ChipStrip<T extends string>({
  options,
  selected,
  onToggle,
  scroll = false,
}: {
  options: ReadonlyArray<{ value: T; label: string }>;
  selected: T[];
  onToggle: (value: T) => void;
  /**
   * Lay the chips out in ONE horizontally-scrolling row instead of wrapping.
   *
   * Opt-in, because it only pays off past a certain count. The three
   * procurement kinds fit on a line and should stay wrapped — a scroller there
   * would hide options behind a gesture for no reason. The fourteen regions
   * are the case it exists for: wrapped, they push the rest of the form down by
   * several rows on a phone.
   */
  scroll?: boolean;
}) {
  return (
    <ul
      className={
        scroll
          ? // Scrolls WITHIN the form's own padding — no negative-margin bleed
            // to the sheet edge. Bleeding made the last chip touch the border
            // with nothing around it, which reads as a clipping bug rather than
            // as "there is more this way".
            'flex flex-nowrap gap-1.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
          : 'flex flex-wrap gap-1.5'
      }
    >
      {options.map((opt) => {
        const on = selected.includes(opt.value);
        return (
          <li key={opt.value} className={scroll ? 'shrink-0' : undefined}>
            <button
              type="button"
              aria-pressed={on}
              onClick={() => onToggle(opt.value)}
              className={`rounded-md border-[1.5px] px-2.5 py-1 text-[12.5px] font-semibold transition-colors ${
                on
                  ? 'border-brand bg-brand-dim text-brand'
                  : 'border-edge bg-bone-surface text-ink-soft hover:border-brand'
              }`}
            >
              {opt.label}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
